import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Text,
  useToast,
} from '@chatsdet/ui';
import { useNavigate } from '@tanstack/react-router';
import {
  useDeleteInstance,
  useEnvironmentInstances,
  type EnvironmentInstanceStatus,
} from '../../../hooks/useEnvironmentInstances';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';
import { CreateInstanceDialog } from './CreateInstanceDialog';
import { InstancesTab } from './InstancesTab';
import { buildTemplateInstanceNavigationTarget } from './template-instance-navigation';

const TERMINAL_INSTANCE_STATUSES = new Set<EnvironmentInstanceStatus>(['terminated', 'failed']);

export type TemplateInstanceStats = {
  total: number;
  active: number;
};

type TemplateInstancesSectionProps = {
  template: EnvironmentTemplate;
  templateId: string;
  onStatsChange: (stats: TemplateInstanceStats) => void;
};

export const TemplateInstancesSection: React.FC<TemplateInstancesSectionProps> = ({
  template,
  templateId,
  onStatsChange,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [createInstanceOpen, setCreateInstanceOpen] = React.useState(false);
  const [deleteInstanceConfirmId, setDeleteInstanceConfirmId] = React.useState<string | null>(null);

  const instancesQuery = useEnvironmentInstances({ templateId });
  const deleteInstanceMutation = useDeleteInstance();
  const instances = instancesQuery.data?.data ?? [];
  const activeInstanceCount = instances.filter(
    (instance) => !TERMINAL_INSTANCE_STATUSES.has(instance.status)
  ).length;
  const stats = React.useMemo<TemplateInstanceStats>(
    () => ({ total: instances.length, active: activeInstanceCount }),
    [activeInstanceCount, instances.length]
  );

  React.useEffect(() => {
    onStatsChange(stats);
  }, [onStatsChange, stats]);

  const handleDeleteInstance = async () => {
    if (!deleteInstanceConfirmId) return;
    try {
      await deleteInstanceMutation.mutateAsync(deleteInstanceConfirmId);
      toast.success('Instance deleted');
      setDeleteInstanceConfirmId(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete instance');
    }
  };

  return (
    <>
      <InstancesTab
        template={template}
        instances={instances}
        isLoading={instancesQuery.isLoading}
        isDeleting={deleteInstanceMutation.isPending}
        onLaunch={() => setCreateInstanceOpen(true)}
        onViewInstance={(instance) => navigate(buildTemplateInstanceNavigationTarget(instance))}
        onDelete={setDeleteInstanceConfirmId}
      />

      <CreateInstanceDialog
        template={template}
        isOpen={createInstanceOpen}
        onOpenChange={setCreateInstanceOpen}
        onSuccess={() => instancesQuery.refetch()}
      />

      <DialogTrigger
        isOpen={!!deleteInstanceConfirmId}
        onOpenChange={(open) => !open && setDeleteInstanceConfirmId(null)}
        size="sm"
      >
        <span />
        <Dialog
          title="Delete Instance"
          description="Are you sure you want to delete this instance?"
        >
          <DialogContent>
            <Text>
              This will permanently delete the instance record. This action cannot be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setDeleteInstanceConfirmId(null)}
              isDisabled={deleteInstanceMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={handleDeleteInstance}
              isDisabled={deleteInstanceMutation.isPending}
            >
              {deleteInstanceMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
};
